import Big from 'big.js';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { twc } from 'react-twc';
import useSWR from 'swr';

import { GlobalContainer } from '@/context/GlobalContext';
import RestakeTabContext from '@/context/RestakeTabContext';
import SymbolInputContext from '@/context/SymbolInputContext';
import TokenInputContext from '@/context/TokenInputContext';
import { useBalance } from '@/hooks/useBalance';
import { formatShownDigit } from '@/utils/number';
import { getPriceInUsd } from '@/utils/price';
import { isDigit, isInputDigit, isMaxDecimals, removePrefix0 } from '@/utils/regex';
import { cn } from '@/utils/styles';
import toLocaleString from '@/utils/toLocaleString';

import { InputAreaContainer, InputRow, TokenInputContainer, TokenLabel } from './styles';

const { useSymbolInputTracked } = SymbolInputContext;
const { useTokenInputTracked } = TokenInputContext;
const { useRestakeTabTracked } = RestakeTabContext;

export default function TokenAmountInput() {
  const { t } = useTranslation();

  const { sdk } = GlobalContainer.useContainer();
  const [symbolInput] = useSymbolInputTracked();
  const [tokenInput, setTokenInput] = useTokenInputTracked();
  const [restakeTabTracked] = useRestakeTabTracked();

  const { shownBalance } = useBalance();
  const [error, setError] = useState('');

  const { data: price } = useSWR(
    `getPriceInUsd_${symbolInput?.address}_${symbolInput?.symbol}_${!!sdk ? 'true' : 'false'}`,
    async () => {
      if (!sdk || !symbolInput) return undefined;
      return await getPriceInUsd(sdk, symbolInput.address, symbolInput.symbol);
    },
  );

  const tokenInputInUsd = useMemo(() => {
    if (!isDigit(tokenInput) || !price) return '-';
    return toLocaleString(price.times(tokenInput).toFixed(), 2);
  }, [tokenInput, price]);

  // empty error when toggle tab
  useEffect(() => {
    setError('');
    setTokenInput('');
  }, [restakeTabTracked, symbolInput, setTokenInput]);

  if (!symbolInput) return null;

  return (
    <InputAreaContainer>
      <TokenLabel>
        {t('restake.enterAmount', {
          amt: symbolInput.symbol,
        })}
      </TokenLabel>
      <TokenInputContainer className={cn('flex-col gap-3', error !== '' && '!border-[#D92D20]')}>
        <InputRow>
          <input
            className="flex-1 text-lg md:text-[28px] font-bold outline-none bg-transparent placeholder-opacity-30 placeholder-white"
            value={tokenInput}
            onChange={(e) => {
              setError('');
              const value = removePrefix0(e.target.value);
              if (
                (isInputDigit(value) && !isDigit(value)) ||
                (isDigit(value) && isMaxDecimals(value, symbolInput.decimals))
              ) {
                if (
                  restakeTabTracked === 'deposit' &&
                  isDigit(value) &&
                  Big(value).lt(symbolInput.minAmount)
                ) {
                  setError(
                    t('error.minimumAmount', {
                      minAmount: symbolInput.minAmount,
                      symbol: symbolInput.symbol,
                    }),
                  );
                }
                setTokenInput(value);
              }
            }}
            placeholder={`$${symbolInput.symbol} amount`}
          />
          <div
            className="px-3 py-1 text-xs font-semibold border border-white rounded-md md:px-4 md:text-sm border-opacity-10 text-[rgba(255,255,255,0.8)]"
            onClick={() => {
              setError('');
              if (shownBalance && isDigit(shownBalance)) {
                if (
                  restakeTabTracked === 'deposit' &&
                  shownBalance &&
                  Big(shownBalance).lt(symbolInput.minAmount)
                ) {
                  setError(
                    t('error.minimumAmount', {
                      minAmount: symbolInput.minAmount,
                      symbol: symbolInput.symbol,
                    }),
                  );
                }
                setTokenInput(shownBalance);
              }
            }}
          >
            {t('restake.max')}
          </div>
        </InputRow>
        <InputRow>
          <SubParagraph>${tokenInputInUsd}</SubParagraph>
          <SubParagraph>
            {t('restake.balance')}: {formatShownDigit(shownBalance)}
          </SubParagraph>
        </InputRow>
      </TokenInputContainer>
      {error && <div className="text-[#D92D20] text-sm mt-1">{error}</div>}
    </InputAreaContainer>
  );
}

const SubParagraph = twc.div`
  text-white text-opacity-40
  text-xs md:text-sm
`;
